import {downloadBlobAsFile} from '@Utils/file.util';
import type {AgentOverrideRow, ForcedGeminiImageModel, ForcedImageProvider, ForcedModelOption, ForcedProviderOption} from './ai-chat-super-user-options.types';

const BASE = 'ai';

const FORCED_IMAGE_PROVIDERS: readonly ForcedImageProvider[] = ['auto', 'gemini-3.1-flash-image-preview', 'openai-gpt-image', 'seedream'];

export const IMAGE_PROVIDER_LABELS: Record<Exclude<ForcedImageProvider, 'auto'>, string> = {
  'gemini-3.1-flash-image-preview': 'Nano Banana 2',
  'openai-gpt-image': 'GPT Image 2',
  seedream: 'Seedream',
};

const GEMINI_MODEL_VERSION_LABELS: Record<string, string> = {
  'gemini-3.1-flash-image': 'Nano Banana 2 (GA)',
  'gemini-3.1-flash-image-preview': 'Nano Banana 2 (preview)',
};

export function getImageProviderLabel(provider: string, model?: string | null): string {
  if (model && model in GEMINI_MODEL_VERSION_LABELS) {
    return GEMINI_MODEL_VERSION_LABELS[model];
  }
  if (!isForcedImageProvider(provider) || provider === 'auto') {
    return provider;
  }
  return IMAGE_PROVIDER_LABELS[provider];
}

export function getInitialAgents(): AgentOverrideRow[] {
  const raw: unknown = window.reactPageProps?.aiChatSuperUserVariantOverrides;
  return Array.isArray(raw) ? (raw as AgentOverrideRow[]) : [];
}

export function setSuperUserVariantOverride(agentType: string, alias: string): Promise<void> {
  return window.PMW.writeLocal(BASE + '/setSuperUserVariantOverride', {agentType, alias}) as Promise<void>;
}

export function clearSuperUserVariantOverrides(): Promise<void> {
  return window.PMW.writeLocal(BASE + '/clearSuperUserVariantOverrides', {}) as Promise<void>;
}

export async function downloadUserAttachments(chatId: string): Promise<void> {
  const params = new URLSearchParams({chatId});
  const url = `${window.PMW.util.site_url(BASE + '/superUserDownloadAllChatAttachments')}?${params.toString()}`;

  const res = await fetch(url, {credentials: 'same-origin'});
  if (!res.ok || isJsonResponse(res)) {
    throw await readSuperUserDownloadError(res);
  }

  const blob = await res.blob();
  downloadBlobAsFile(blob, `aichat-attachments-${chatId}.zip`);
}

function isJsonResponse(res: Response): boolean {
  const contentType = res.headers.get('Content-Type') ?? '';
  return contentType.toLowerCase().includes('application/json');
}

async function readSuperUserDownloadError(res: Response): Promise<Error> {
  const fallback = new Error(`Download failed: ${res.status}`);
  try {
    const body = (await res.json()) as {message?: string; status?: string};
    return body?.message ? new Error(body.message) : fallback;
  } catch {
    return fallback;
  }
}

export function isForcedImageProvider(value: string): value is ForcedImageProvider {
  return FORCED_IMAGE_PROVIDERS.some((provider) => provider === value);
}

export function getInitialForcedImageProvider(): ForcedImageProvider {
  const raw: unknown = window.reactPageProps?.aiChatForcedImageProvider;
  return typeof raw === 'string' && isForcedImageProvider(raw) ? raw : 'auto';
}

export function setSuperUserForcedImageProvider(provider: ForcedImageProvider): Promise<void> {
  return window.PMW.writeLocal(BASE + '/setSuperUserForcedImageProvider', {provider}) as Promise<void>;
}

export function getForcedImageProviderOptions(): ForcedProviderOption[] {
  return FORCED_IMAGE_PROVIDERS.map((value) => ({
    value,
    label: value === 'auto' ? window.i18next.t('pmwjs_ai_chat_provider_auto') : IMAGE_PROVIDER_LABELS[value],
  }));
}

const FORCED_GEMINI_IMAGE_MODELS: readonly ForcedGeminiImageModel[] = ['auto', 'preview', 'ga'];

const GEMINI_IMAGE_MODEL_LABELS: Record<Exclude<ForcedGeminiImageModel, 'auto'>, string> = {
  preview: 'Preview (gemini-3.1-flash-image-preview)',
  ga: 'GA (gemini-3.1-flash-image)',
};

export function isForcedGeminiImageModel(value: string): value is ForcedGeminiImageModel {
  return FORCED_GEMINI_IMAGE_MODELS.some((model) => model === value);
}

export function getInitialForcedGeminiImageModel(): ForcedGeminiImageModel {
  const raw: unknown = window.reactPageProps?.aiChatForcedGeminiImageModel;
  return typeof raw === 'string' && isForcedGeminiImageModel(raw) ? raw : 'auto';
}

export function setSuperUserForcedGeminiImageModel(model: ForcedGeminiImageModel): Promise<void> {
  return window.PMW.writeLocal(BASE + '/setSuperUserForcedGeminiImageModel', {model}) as Promise<void>;
}

export function getForcedGeminiImageModelOptions(): ForcedModelOption[] {
  return FORCED_GEMINI_IMAGE_MODELS.map((value) => ({
    value,
    label: value === 'auto' ? window.i18next.t('pmwjs_ai_chat_provider_auto') : GEMINI_IMAGE_MODEL_LABELS[value],
  }));
}
